import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaImage, FaSpinner, FaSave, FaEye, FaTrash, FaUpload, FaBars, FaPalette, FaBell, FaLock, FaStar, FaMapMarkerAlt, FaMobileAlt } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';


function SettingsPage() {
    const navigate = useNavigate();
    const [imageUploading, setImageUploading] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false
    });
    const [offerImageUploading, setOfferImageUploading] = useState({
        offer1: false,
        offer1Locked: false,
        offer1Unlocked: false,
        offer2: false,
        offer2Locked: false,
        offer2Unlocked: false,
        offer3: false,
        offer3Locked: false,
        offer3Unlocked: false,
        promotionImage: false,
        promotionVideo: false,
    });
    const [imgData, setImgData] = useState([]);
    const [offerImgData, setOfferImgData] = useState({});
    const [loading, setLoading] = useState(true);
    const [offerImageLoading, setOfferImageLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('banner');

    // Onboarding screens state
    const [onboardingTitle1, setOnboardingTitle1] = useState('');
    const [onboardingSubtitle1, setOnboardingSubtitle1] = useState('');
    const [onboardingImage1, setOnboardingImage1] = useState('');
    const [onboardingTitle2, setOnboardingTitle2] = useState('');
    const [onboardingSubtitle2, setOnboardingSubtitle2] = useState('');
    const [onboardingImage2, setOnboardingImage2] = useState('');
    const [onboardingTitle3, setOnboardingTitle3] = useState('');
    const [onboardingSubtitle3, setOnboardingSubtitle3] = useState('');
    const [onboardingImage3, setOnboardingImage3] = useState('');
    const [onboardingImageUploading, setOnboardingImageUploading] = useState({
        onboardingImage1: false,
        onboardingImage2: false,
        onboardingImage3: false,
    });

    // Change password state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const [pwSuccess, setPwSuccess] = useState(false);
    const [pwError, setPwError] = useState('');

    // Homepage message state
    const [homepageMainLine, setHomepageMainLine] = useState('');
    const [homepageSubLine, setHomepageSubLine] = useState('');
    const [supportPhoneNumber, setSupportPhoneNumber] = useState('');
    const [offerLine1, setOfferLine1] = useState('');
    const [offerLine2, setOfferLine2] = useState('');
    const [offerLine3, setOfferLine3] = useState('');
    // Delivery/handling charges state
    const [freeDeliveryAbove, setFreeDeliveryAbove] = useState('');
    const [deliveryCharge, setDeliveryCharge] = useState('');
    const [handlingCharge, setHandlingCharge] = useState('');
    const [storeLatitude, setStoreLatitude] = useState('');
    const [storeLongitude, setStoreLongitude] = useState('');
    const [maxDeliveryRadius, setMaxDeliveryRadius] = useState('');
    const [localZoneRadius, setLocalZoneRadius] = useState('');
    const [localFreeDeliveryAbove, setLocalFreeDeliveryAbove] = useState('');
    const [localDeliveryThresholdLimit, setLocalDeliveryThresholdLimit] = useState('');
    const [localDeliveryUnder100, setLocalDeliveryUnder100] = useState('');
    const [localDeliveryUnder200, setLocalDeliveryUnder200] = useState('');
    const [localHandlingCharge, setLocalHandlingCharge] = useState('');
    const [localHandlingUnderThreshold, setLocalHandlingUnderThreshold] = useState('');
    const [localHandlingOverThreshold, setLocalHandlingOverThreshold] = useState('');
    const [outerZoneMinOrder, setOuterZoneMinOrder] = useState('');
    const [outerZoneDeliveryCharge, setOuterZoneDeliveryCharge] = useState('');
    const [basePrepTime, setBasePrepTime] = useState('');
    const [deliveryTimePerKm, setDeliveryTimePerKm] = useState('');
    const [isDeliveryOpen, setIsDeliveryOpen] = useState(true);
    const [deliveryClosedMessage, setDeliveryClosedMessage] = useState('');
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [settingsSuccess, setSettingsSuccess] = useState(false);

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const handleFetchCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser");
            return;
        }
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setStoreLatitude(position.coords.latitude);
                setStoreLongitude(position.coords.longitude);
                setGpsLoading(false);
            },
            (error) => {
                console.error("Error fetching location:", error);
                alert(`Error fetching location: ${error.message}`);
                setGpsLoading(false);
            }
        );
    };

    // Fetch onboarding settings
    const fetchOnboardingSettings = async () => {
        setSettingsLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/settings/onboarding`);
            setOnboardingTitle1(res.data.onboardingTitle1 || '');
            setOnboardingSubtitle1(res.data.onboardingSubtitle1 || '');
            setOnboardingImage1(res.data.onboardingImage1 || '');
            setOnboardingTitle2(res.data.onboardingTitle2 || '');
            setOnboardingSubtitle2(res.data.onboardingSubtitle2 || '');
            setOnboardingImage2(res.data.onboardingImage2 || '');
            setOnboardingTitle3(res.data.onboardingTitle3 || '');
            setOnboardingSubtitle3(res.data.onboardingSubtitle3 || '');
            setOnboardingImage3(res.data.onboardingImage3 || '');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSettingsLoading(false);
        }
    };

    // Save onboarding settings
    const handleSaveOnboardingSettings = async (e) => {
        e.preventDefault();
        setSettingsLoading(true);
        setSettingsSuccess(false);
        try {
            await axios.post(`${API_BASE_URL}/settings/onboarding`, {
                onboardingTitle1,
                onboardingSubtitle1,
                onboardingImage1,
                onboardingTitle2,
                onboardingSubtitle2,
                onboardingImage2,
                onboardingTitle3,
                onboardingSubtitle3,
                onboardingImage3,
            });
            setSettingsSuccess(true);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSettingsLoading(false);
            setTimeout(() => setSettingsSuccess(false), 2000);
        }
    };

    // Handle onboarding image upload
    const handleOnboardingImageUpload = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            setOnboardingImageUploading(prev => ({ ...prev, [imageKey]: true }));
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);
            if (response.status === 200 && response.data.path) {
                if (imageKey === 'onboardingImage1') setOnboardingImage1(response.data.path);
                if (imageKey === 'onboardingImage2') setOnboardingImage2(response.data.path);
                if (imageKey === 'onboardingImage3') setOnboardingImage3(response.data.path);
            }
        } catch (err) {
            handleApiError(err);
        } finally {
            setOnboardingImageUploading(prev => ({ ...prev, [imageKey]: false }));
        }
    };

    const handleImageUpload = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(prev => ({ ...prev, [imageKey]: true }));
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);

            await axios.patch(`${API_BASE_URL}/update-banner-images`, {
                [imageKey]: response.data.path,
            });

            alert('Banner image updated successfully!');
            handleFetchImages();
        } catch (error) {
            handleApiError(error);
        } finally {
            setImageUploading(prev => ({ ...prev, [imageKey]: false }));
        }
    };

    const handleFetchImages = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/slider-images`);
            setImgData(response.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch offer images for homepage
    const handleFetchOfferImages = async () => {
        setOfferImageLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/offer-images`);
            setOfferImgData(response.data);
        } catch (error) {
            console.error('Error fetching offer images:', error);
        } finally {
            setOfferImageLoading(false);
        }
    };

    // Handle offer image upload
    const handleOfferImageUpload = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        if (!file) return;

        const isVideo = imageKey === 'promotionVideo';
        const allowedTypes = isVideo 
            ? ['video/mp4', 'video/webm', 'video/ogg']
            : ['image/jpeg', 'image/png', 'image/webp'];
        
        if (!allowedTypes.includes(file.type)) {
            alert(isVideo ? 'Only MP4, WebM, or OGG videos are allowed.' : 'Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        const sizeLimit = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for image
        if (file.size > sizeLimit) {
            alert(`File size must be under ${isVideo ? '50MB' : '5MB'}.`);
            return;
        }

        try {
            setOfferImageUploading(prev => ({ ...prev, [imageKey]: true }));
            const formData = new FormData();
            formData.append(isVideo ? 'video' : 'image', file);
            const uploadEndpoint = isVideo ? `${API_BASE_URL}/upload-video` : `${API_BASE_URL}/upload-prod-img`;
            const response = await axios.post(uploadEndpoint, formData);

            await axios.patch(`${API_BASE_URL}/update-offer-images`, {
                [imageKey]: response.data.path,
            });

            alert(`${isVideo ? 'Promotion video' : 'Offer image'} updated successfully!`);
            handleFetchOfferImages();
        } catch (error) {
            handleApiError(error);
        } finally {
            setOfferImageUploading(prev => ({ ...prev, [imageKey]: false }));
        }
    };


    // Fetch homepage message when General tab is active
    useEffect(() => {
        handleFetchImages();
        handleFetchOfferImages();
    }, []);

    useEffect(() => {
        if (activeTab === 'general' || activeTab === 'delivery') {
            fetchGeneralSettings();
        } else if (activeTab === 'onboarding') {
            fetchOnboardingSettings();
        }
        // eslint-disable-next-line
    }, [activeTab]);

    // Fetch homepage message and delivery/handling charges
    const fetchGeneralSettings = async () => {
        setSettingsLoading(true);
        try {
            // Fetch homepage message
            const resMsg = await axios.get(`${API_BASE_URL}/settings/homepage-message`);
            setHomepageMainLine(resMsg.data.homepageMainLine || '');
            setHomepageSubLine(resMsg.data.homepageSubLine || '');
            setSupportPhoneNumber(resMsg.data.supportPhoneNumber || '9917463608');
            // Fetch offer tiles
            const resOffers = await axios.get(`${API_BASE_URL}/offer-tiles`);
            setOfferLine1(resOffers.data.offerLine1 || '');
            setOfferLine2(resOffers.data.offerLine2 || '');
            setOfferLine3(resOffers.data.offerLine3 || '');
            // Fetch delivery/handling charges
            const resCharges = await axios.get(`${API_BASE_URL}/get-delivery-handling-charges`);
            setFreeDeliveryAbove(resCharges.data.freeDeliveryAbove ?? '');
            setDeliveryCharge(resCharges.data.deliveryCharge ?? '');
            setHandlingCharge(resCharges.data.handlingCharge ?? '');
            setStoreLatitude(resCharges.data.storeLatitude ?? '');
            setStoreLongitude(resCharges.data.storeLongitude ?? '');
            setMaxDeliveryRadius(resCharges.data.maxDeliveryRadius ?? '');
            setLocalZoneRadius(resCharges.data.localZoneRadius ?? '');
            setLocalFreeDeliveryAbove(resCharges.data.localFreeDeliveryAbove ?? '');
            setLocalDeliveryThresholdLimit(resCharges.data.localDeliveryThresholdLimit ?? '');
            setLocalDeliveryUnder100(resCharges.data.localDeliveryUnder100 ?? '');
            setLocalDeliveryUnder200(resCharges.data.localDeliveryUnder200 ?? '');
            setLocalHandlingCharge(resCharges.data.localHandlingCharge ?? '');
            setLocalHandlingUnderThreshold(resCharges.data.localHandlingUnderThreshold ?? '');
            setLocalHandlingOverThreshold(resCharges.data.localHandlingOverThreshold ?? '');
            setOuterZoneMinOrder(resCharges.data.outerZoneMinOrder ?? '');
            setOuterZoneDeliveryCharge(resCharges.data.outerZoneDeliveryCharge ?? '');
            setBasePrepTime(resCharges.data.basePrepTime ?? 20);
            setDeliveryTimePerKm(resCharges.data.deliveryTimePerKm ?? 3);
            setIsDeliveryOpen(resCharges.data.isDeliveryOpen ?? true);
            setDeliveryClosedMessage(resCharges.data.deliveryClosedMessage ?? '');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSettingsLoading(false);
        }
    };

    // Save homepage messages and offer tiles
    const handleSaveGeneralSettings = async (e) => {
        e.preventDefault();
        setSettingsLoading(true);
        setSettingsSuccess(false);
        try {
            // Save homepage message
            const resMsg = await axios.post(`${API_BASE_URL}/settings/homepage-message`, {
                homepageMainLine,
                homepageSubLine,
                supportPhoneNumber
            });
            setHomepageMainLine(resMsg.data.homepageMainLine || '');
            setHomepageSubLine(resMsg.data.homepageSubLine || '');
            setSupportPhoneNumber(resMsg.data.supportPhoneNumber || '9917463608');
            // Save offer tiles
            const resOffers = await axios.post(`${API_BASE_URL}/offer-tiles`, {
                offerLine1,
                offerLine2,
                offerLine3,
            });
            setOfferLine1(resOffers.data.offerLine1 || '');
            setOfferLine2(resOffers.data.offerLine2 || '');
            setOfferLine3(resOffers.data.offerLine3 || '');
            setSettingsSuccess(true);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSettingsLoading(false);
            setTimeout(() => setSettingsSuccess(false), 2000);
        }
    };

    // Save delivery/handling charges and dynamic coordinates
    const handleSaveDeliverySettings = async (e) => {
        e.preventDefault();
        setSettingsLoading(true);
        setSettingsSuccess(false);
        try {
            const resCharges = await axios.post(`${API_BASE_URL}/set-delivery-handling-charges`, {
                freeDeliveryAbove,
                deliveryCharge,
                handlingCharge,
                storeLatitude,
                storeLongitude,
                maxDeliveryRadius,
                localZoneRadius,
                localFreeDeliveryAbove,
                localDeliveryThresholdLimit,
                localDeliveryUnder100,
                localDeliveryUnder200,
                localHandlingCharge,
                localHandlingUnderThreshold,
                localHandlingOverThreshold,
                outerZoneMinOrder,
                outerZoneDeliveryCharge,
                basePrepTime,
                deliveryTimePerKm,
                isDeliveryOpen,
                deliveryClosedMessage
            });
            setFreeDeliveryAbove(resCharges.data.freeDeliveryAbove ?? '');
            setDeliveryCharge(resCharges.data.deliveryCharge ?? '');
            setHandlingCharge(resCharges.data.handlingCharge ?? '');
            setStoreLatitude(resCharges.data.storeLatitude ?? '');
            setStoreLongitude(resCharges.data.storeLongitude ?? '');
            setMaxDeliveryRadius(resCharges.data.maxDeliveryRadius ?? '');
            setLocalZoneRadius(resCharges.data.localZoneRadius ?? '');
            setLocalFreeDeliveryAbove(resCharges.data.localFreeDeliveryAbove ?? '');
            setLocalDeliveryThresholdLimit(resCharges.data.localDeliveryThresholdLimit ?? '');
            setLocalDeliveryUnder100(resCharges.data.localDeliveryUnder100 ?? '');
            setLocalDeliveryUnder200(resCharges.data.localDeliveryUnder200 ?? '');
            setLocalHandlingCharge(resCharges.data.localHandlingCharge ?? '');
            setLocalHandlingUnderThreshold(resCharges.data.localHandlingUnderThreshold ?? '');
            setLocalHandlingOverThreshold(resCharges.data.localHandlingOverThreshold ?? '');
            setOuterZoneMinOrder(resCharges.data.outerZoneMinOrder ?? '');
            setOuterZoneDeliveryCharge(resCharges.data.outerZoneDeliveryCharge ?? '');
            setBasePrepTime(resCharges.data.basePrepTime ?? 20);
            setDeliveryTimePerKm(resCharges.data.deliveryTimePerKm ?? 3);
            setIsDeliveryOpen(resCharges.data.isDeliveryOpen ?? true);
            setDeliveryClosedMessage(resCharges.data.deliveryClosedMessage ?? '');
            setSettingsSuccess(true);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSettingsLoading(false);
            setTimeout(() => setSettingsSuccess(false), 2000);
        }
    };

    const renderBannerSettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <div className="row">
                    {['image1', 'image2', 'image3', 'image4'].map((imageKey, index) => (
                        <div className="col-lg-6 col-md-6 col-sm-12 mb-4" key={index}>
                            <div className="border rounded-0 p-3" style={{ borderColor: '#d1d5db' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label className="form-label fw-bold mb-0">Banner Image {index + 1}</label>
                                    <small className="text-muted">*Recommended: 1920x600px</small>
                                </div>
                                
                                <div className="text-center mb-3">
                                    {imageUploading[imageKey] ? (
                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
                                            <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                            <small>Uploading...</small>
                                        </div>
                                    ) : imgData[0]?.[imageKey] ? (
                                        <img 
                                            src={`${API_BASE_URL}/${imgData[0][imageKey]}`} 
                                            alt={`Banner ${index + 1}`} 
                                            className="img-fluid rounded-0" 
                                            style={{ maxHeight: '150px', width: 'auto' }}
                                        />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '150px' }}>
                                            <div>
                                                <FaImage className="fs-1 mb-2" />
                                                <p className="mb-0">No image uploaded</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    name={imageKey} 
                                    onChange={handleImageUpload}
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderOnboardingSettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <form onSubmit={handleSaveOnboardingSettings} autoComplete="off">
                    <div className="row">
                        {/* Slide 1 */}
                        <div className="col-md-4 mb-4" style={{ borderRight: '1px solid #eee' }}>
                            <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>SLIDE 1 CONFIGURATION</h6>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Heading</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Slide 1 Heading"
                                    value={onboardingTitle1}
                                    onChange={e => setOnboardingTitle1(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Subheading Description</label>
                                <textarea
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '8px 12px', fontSize: '13px', minHeight: '80px' }}
                                    placeholder="Slide 1 Subheading Description"
                                    value={onboardingSubtitle1}
                                    onChange={e => setOnboardingSubtitle1(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Slide 1 Image</label>
                                <div style={{ border: '1px dashed #ccc', padding: '15px', textAlign: 'center', backgroundColor: '#fafafa', position: 'relative' }}>
                                    {onboardingImageUploading.onboardingImage1 ? (
                                        <div className="py-2"><FaSpinner className="spinner-border spinner-border-sm" /></div>
                                    ) : onboardingImage1 ? (
                                        <div>
                                            <img
                                                src={`${API_BASE_URL}/${onboardingImage1}`}
                                                alt="Slide 1 preview"
                                                style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '8px' }}
                                            />
                                            <small className="text-success d-block">✓ Image uploaded</small>
                                        </div>
                                    ) : (
                                        <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>No image. Circular icon will be used.</p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        name="onboardingImage1"
                                        onChange={handleOnboardingImageUpload}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    />
                                </div>
                                {onboardingImage1 && (
                                    <button
                                        type="button"
                                        className="btn btn-link text-danger p-0 mt-1"
                                        style={{ fontSize: '11px' }}
                                        onClick={() => setOnboardingImage1('')}
                                    >
                                        Remove Image
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Slide 2 */}
                        <div className="col-md-4 mb-4" style={{ borderRight: '1px solid #eee' }}>
                            <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>SLIDE 2 CONFIGURATION</h6>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Heading</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Slide 2 Heading"
                                    value={onboardingTitle2}
                                    onChange={e => setOnboardingTitle2(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Subheading Description</label>
                                <textarea
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '8px 12px', fontSize: '13px', minHeight: '80px' }}
                                    placeholder="Slide 2 Subheading Description"
                                    value={onboardingSubtitle2}
                                    onChange={e => setOnboardingSubtitle2(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Slide 2 Image</label>
                                <div style={{ border: '1px dashed #ccc', padding: '15px', textAlign: 'center', backgroundColor: '#fafafa', position: 'relative' }}>
                                    {onboardingImageUploading.onboardingImage2 ? (
                                        <div className="py-2"><FaSpinner className="spinner-border spinner-border-sm" /></div>
                                    ) : onboardingImage2 ? (
                                        <div>
                                            <img
                                                src={`${API_BASE_URL}/${onboardingImage2}`}
                                                alt="Slide 2 preview"
                                                style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '8px' }}
                                            />
                                            <small className="text-success d-block">✓ Image uploaded</small>
                                        </div>
                                    ) : (
                                        <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>No image. Circular icon will be used.</p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        name="onboardingImage2"
                                        onChange={handleOnboardingImageUpload}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    />
                                </div>
                                {onboardingImage2 && (
                                    <button
                                        type="button"
                                        className="btn btn-link text-danger p-0 mt-1"
                                        style={{ fontSize: '11px' }}
                                        onClick={() => setOnboardingImage2('')}
                                    >
                                        Remove Image
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Slide 3 */}
                        <div className="col-md-4 mb-4">
                            <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>SLIDE 3 CONFIGURATION</h6>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Heading</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Slide 3 Heading"
                                    value={onboardingTitle3}
                                    onChange={e => setOnboardingTitle3(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Subheading Description</label>
                                <textarea
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '8px 12px', fontSize: '13px', minHeight: '80px' }}
                                    placeholder="Slide 3 Subheading Description"
                                    value={onboardingSubtitle3}
                                    onChange={e => setOnboardingSubtitle3(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Slide 3 Image</label>
                                <div style={{ border: '1px dashed #ccc', padding: '15px', textAlign: 'center', backgroundColor: '#fafafa', position: 'relative' }}>
                                    {onboardingImageUploading.onboardingImage3 ? (
                                        <div className="py-2"><FaSpinner className="spinner-border spinner-border-sm" /></div>
                                    ) : onboardingImage3 ? (
                                        <div>
                                            <img
                                                src={`${API_BASE_URL}/${onboardingImage3}`}
                                                alt="Slide 3 preview"
                                                style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '8px' }}
                                            />
                                            <small className="text-success d-block">✓ Image uploaded</small>
                                        </div>
                                    ) : (
                                        <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>No image. Circular icon will be used.</p>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        name="onboardingImage3"
                                        onChange={handleOnboardingImageUpload}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    />
                                </div>
                                {onboardingImage3 && (
                                    <button
                                        type="button"
                                        className="btn btn-link text-danger p-0 mt-1"
                                        style={{ fontSize: '11px' }}
                                        onClick={() => setOnboardingImage3('')}
                                    >
                                        Remove Image
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="text-end d-flex align-items-center justify-content-end gap-3 mt-3">
                        {settingsSuccess && (
                            <span className="text-success fw-bold" style={{ fontSize: '13px' }}>Saved successfully!</span>
                        )}
                        <button
                            type="submit"
                            className="btn theme-btn-blue-flat btn-sm rounded-0"
                            style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', height: '38px', display: 'flex', alignItems: 'center', justify_content: 'center', gap: '6px' }}
                            disabled={settingsLoading}
                        >
                            {settingsLoading ? (
                                <>
                                    <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Save Onboarding Settings
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    const renderGeneralSettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <form onSubmit={handleSaveGeneralSettings} autoComplete="off">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Homepage Main Line</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter main line for homepage"
                                    value={homepageMainLine}
                                    onChange={e => setHomepageMainLine(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={100}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Homepage Sub Line</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter sub line for homepage"
                                    value={homepageSubLine}
                                    onChange={e => setHomepageSubLine(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={200}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Support Phone Number</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter support phone number"
                                    value={supportPhoneNumber}
                                    onChange={e => setSupportPhoneNumber(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={15}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Offer Line 1</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter first offer line"
                                    value={offerLine1}
                                    onChange={e => setOfferLine1(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={150}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Offer Line 2</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter second offer line"
                                    value={offerLine2}
                                    onChange={e => setOfferLine2(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={150}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Offer Line 3</label>
                                <input
                                    type="text"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    placeholder="Enter third offer line"
                                    value={offerLine3}
                                    onChange={e => setOfferLine3(e.target.value)}
                                    disabled={settingsLoading}
                                    maxLength={150}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="text-end d-flex align-items-center justify-content-end gap-3">
                        {settingsSuccess && (
                            <span className="text-success fw-bold" style={{ fontSize: '13px' }}>Saved successfully!</span>
                        )}
                        <button
                            type="submit"
                            className="btn theme-btn-blue-flat btn-sm rounded-0"
                            style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                            disabled={settingsLoading}
                        >
                            {settingsLoading ? (
                                <>
                                    <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    const renderDeliverySettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <form onSubmit={handleSaveDeliverySettings} autoComplete="off">
                    <div className="row">
                            <div className="mb-4 p-3" style={{ backgroundColor: '#f8fafc', borderLeft: '4px solid #1a237e' }}>
                                <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>DELIVERY STATUS & HOLIDAYS</h6>
                                <div className="row">
                                    <div className="col-md-4 mb-3 d-flex flex-column justify-content-center">
                                        <label className="form-label fw-bold mb-2" style={{ fontSize: '12px', color: '#64748b' }}>Delivery Service Status</label>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="isDeliveryOpenSwitch"
                                                style={{ width: '40px', height: '20px', cursor: 'pointer' }}
                                                checked={isDeliveryOpen}
                                                onChange={e => setIsDeliveryOpen(e.target.checked)}
                                                disabled={settingsLoading}
                                            />
                                            <label className={`form-check-label ms-2 fw-bold ${isDeliveryOpen ? 'text-success' : 'text-danger'}`} htmlFor="isDeliveryOpenSwitch" style={{ fontSize: '13px' }}>
                                                {isDeliveryOpen ? 'ACTIVE / OPEN' : 'CLOSED'}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-8 mb-3">
                                        <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Closed Status Notice Message</label>
                                        <input
                                            type="text" className="form-control rounded-0"
                                            style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                            placeholder="We are currently closed for delivery..."
                                            value={deliveryClosedMessage} onChange={e => setDeliveryClosedMessage(e.target.value)}
                                            disabled={settingsLoading || isDeliveryOpen}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className="fw-bold mb-0 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>STORE GPS LOCATION & LIMITS</h6>
                                <button
                                    type="button"
                                    onClick={handleFetchCurrentLocation}
                                    className="btn btn-outline-primary btn-sm rounded-0 d-flex align-items-center gap-1"
                                    style={{ fontSize: '11px', padding: '4px 10px', height: '28px' }}
                                    disabled={settingsLoading || gpsLoading}
                                >
                                    {gpsLoading ? (
                                        <>
                                            <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Fetching...
                                        </>
                                    ) : (
                                        <>
                                            <FaMapMarkerAlt /> Fetch Current GPS
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Store Latitude</label>
                                    <input
                                        type="number" step="any" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={storeLatitude} onChange={e => setStoreLatitude(e.target.value)}
                                        disabled={settingsLoading} required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Store Longitude</label>
                                    <input
                                        type="number" step="any" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={storeLongitude} onChange={e => setStoreLongitude(e.target.value)}
                                        disabled={settingsLoading} required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Max Delivery Radius (km)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={maxDeliveryRadius} onChange={e => setMaxDeliveryRadius(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Local Zone Radius (km)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localZoneRadius} onChange={e => setLocalZoneRadius(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Base Prep Time (minutes)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={basePrepTime} onChange={e => setBasePrepTime(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Delivery Time Per KM (minutes)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={deliveryTimePerKm} onChange={e => setDeliveryTimePerKm(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>

                            <hr className="my-3" />
                            <h6 className="fw-bold mb-3 text-success" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>LOCAL ZONE CHARGES (Distance ≤ Local Radius)</h6>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Free Delivery Above (₹)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localFreeDeliveryAbove} onChange={e => setLocalFreeDeliveryAbove(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Delivery & Handling Threshold Limit (₹)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localDeliveryThresholdLimit} onChange={e => setLocalDeliveryThresholdLimit(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Delivery Charge (Cart &lt; ₹{localDeliveryThresholdLimit || 100})</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localDeliveryUnder100} onChange={e => setLocalDeliveryUnder100(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Delivery Charge (Cart ₹{localDeliveryThresholdLimit || 100} - ₹{(Number(localFreeDeliveryAbove || 200) - 1)})</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localDeliveryUnder200} onChange={e => setLocalDeliveryUnder200(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Handling Charge (Cart &lt; ₹{localDeliveryThresholdLimit || 100})</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localHandlingUnderThreshold} onChange={e => setLocalHandlingUnderThreshold(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Handling Charge (Cart ₹{localDeliveryThresholdLimit || 100} - ₹{(Number(localFreeDeliveryAbove || 200) - 1)})</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={localHandlingOverThreshold} onChange={e => setLocalHandlingOverThreshold(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>

                            <hr className="my-3" />
                            <h6 className="fw-bold mb-3 text-warning" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>OUTER ZONE CHARGES (Local Radius &lt; Distance ≤ Max Radius)</h6>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Min Order Value for Outer Zone (₹)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={outerZoneMinOrder} onChange={e => setOuterZoneMinOrder(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#64748b' }}>Delivery Charge (₹)</label>
                                    <input
                                        type="number" className="form-control rounded-0"
                                        style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                        value={outerZoneDeliveryCharge} onChange={e => setOuterZoneDeliveryCharge(e.target.value)}
                                        disabled={settingsLoading} min="0" required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="text-end d-flex align-items-center justify-content-end gap-3">
                        {settingsSuccess && (
                            <span className="text-success fw-bold" style={{ fontSize: '13px' }}>Saved successfully!</span>
                        )}
                        <button
                            type="submit"
                            className="btn theme-btn-blue-flat btn-sm rounded-0"
                            style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                            disabled={settingsLoading}
                        >
                            {settingsLoading ? (
                                <>
                                    <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    const renderOfferImagesSettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                {/* Silver Offer (List 1) */}
                <div className="mb-5">
                    <h6 className="fw-bold mb-3" style={{color: '#c0a000'}}>
                        <FaStar className="me-2" />Silver Card Offer - 299
                    </h6>
                    <div className="row">
                        {['offer1', 'offer1Locked', 'offer1Unlocked'].map((imageKey, index) => {
                            const labels = ['Main Image', 'Locked State', 'Unlocked State'];
                            return (
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={imageKey}>
                                    <div className="border rounded-0 p-3" style={{borderColor: '#c0a000'}}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label fw-bold mb-0">{labels[index]}</label>
                                            <small className="text-muted">*Recommended: 600x400px</small>
                                        </div>
                                        
                                        <div className="text-center mb-3">
                                            {offerImageUploading[imageKey] ? (
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                        <small>Uploading...</small>
                                                    </div>
                                                </div>
                                            ) : offerImgData?.[imageKey] ? (
                                                <div className="position-relative">
                                                    <img 
                                                        src={`${API_BASE_URL}/${offerImgData[imageKey]}`} 
                                                        alt={labels[index]} 
                                                        className="img-fluid rounded-0" 
                                                        style={{ maxHeight: '200px', width: 'auto', objectFit: 'cover' }}
                                                    />
                                                    <small className="text-success d-block mt-2">✓ Image uploaded</small>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center text-muted rounded-0" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaImage className="fs-1 mb-2" style={{color: '#ccc'}} />
                                                        <p className="mb-0" style={{fontSize: '12px'}}>No image uploaded</p>
                                                        <p className="mb-0" style={{fontSize: '11px', color: '#999'}}>Click below to upload</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            name={imageKey} 
                                            onChange={handleOfferImageUpload}
                                            className="form-control rounded-0"
                                            style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                            disabled={offerImageUploading[imageKey]}
                                        />
                                        <small className="text-muted d-block mt-2">JPG, PNG, or WebP • Max 5MB</small>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <hr />

                {/* Golden Offer (List 2) */}
                <div className="mb-5">
                    <h6 className="fw-bold mb-3" style={{color: '#ffd600'}}>
                        <FaStar className="me-2" />Golden Card Offer - 499
                    </h6>
                    <div className="row">
                        {['offer2', 'offer2Locked', 'offer2Unlocked'].map((imageKey, index) => {
                            const labels = ['Main Image', 'Locked State', 'Unlocked State'];
                            return (
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={imageKey}>
                                    <div className="border rounded-0 p-3" style={{borderColor: '#ffd600'}}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label fw-bold mb-0">{labels[index]}</label>
                                            <small className="text-muted">*Recommended: 600x400px</small>
                                        </div>
                                        
                                        <div className="text-center mb-3">
                                            {offerImageUploading[imageKey] ? (
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                        <small>Uploading...</small>
                                                    </div>
                                                </div>
                                            ) : offerImgData?.[imageKey] ? (
                                                <div className="position-relative">
                                                    <img 
                                                        src={`${API_BASE_URL}/${offerImgData[imageKey]}`} 
                                                        alt={labels[index]} 
                                                        className="img-fluid rounded-0" 
                                                        style={{ maxHeight: '200px', width: 'auto', objectFit: 'cover' }}
                                                    />
                                                    <small className="text-success d-block mt-2">✓ Image uploaded</small>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center text-muted rounded-0" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaImage className="fs-1 mb-2" style={{color: '#ccc'}} />
                                                        <p className="mb-0" style={{fontSize: '12px'}}>No image uploaded</p>
                                                        <p className="mb-0" style={{fontSize: '11px', color: '#999'}}>Click below to upload</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            name={imageKey} 
                                            onChange={handleOfferImageUpload}
                                            className="form-control rounded-0"
                                            style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                            disabled={offerImageUploading[imageKey]}
                                        />
                                        <small className="text-muted d-block mt-2">JPG, PNG, or WebP • Max 5MB</small>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <hr />

                {/* Diamond Offer (List 3) */}
                <div className="mb-5">
                    <h6 className="fw-bold mb-3" style={{color: '#4fc3f7'}}>
                        <FaStar className="me-2" />Diamond Card Offer - 799
                    </h6>
                    <div className="row">
                        {['offer3', 'offer3Locked', 'offer3Unlocked'].map((imageKey, index) => {
                            const labels = ['Main Image', 'Locked State', 'Unlocked State'];
                            return (
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={imageKey}>
                                    <div className="border rounded-0 p-3" style={{borderColor: '#4fc3f7'}}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label fw-bold mb-0">{labels[index]}</label>
                                            <small className="text-muted">*Recommended: 600x400px</small>
                                        </div>
                                        
                                        <div className="text-center mb-3">
                                            {offerImageUploading[imageKey] ? (
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                        <small>Uploading...</small>
                                                    </div>
                                                </div>
                                            ) : offerImgData?.[imageKey] ? (
                                                <div className="position-relative">
                                                    <img 
                                                        src={`${API_BASE_URL}/${offerImgData[imageKey]}`} 
                                                        alt={labels[index]} 
                                                        className="img-fluid rounded-0" 
                                                        style={{ maxHeight: '200px', width: 'auto', objectFit: 'cover' }}
                                                    />
                                                    <small className="text-success d-block mt-2">✓ Image uploaded</small>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center text-muted rounded-0" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                                    <div>
                                                        <FaImage className="fs-1 mb-2" style={{color: '#ccc'}} />
                                                        <p className="mb-0" style={{fontSize: '12px'}}>No image uploaded</p>
                                                        <p className="mb-0" style={{fontSize: '11px', color: '#999'}}>Click below to upload</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            name={imageKey} 
                                            onChange={handleOfferImageUpload}
                                            className="form-control rounded-0"
                                            style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                            disabled={offerImageUploading[imageKey]}
                                        />
                                        <small className="text-muted d-block mt-2">JPG, PNG, or WebP • Max 5MB</small>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <hr />

                {/* Promotion Image and Video */}
                <div className="mb-3">
                    <h6 className="fw-bold mb-3" style={{color: '#e91e63'}}>
                        <FaImage className="me-2" />Promotion Media
                    </h6>
                    <div className="row">
                        {/* Promotion Image */}
                        <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                            <div className="border rounded-0 p-3" style={{borderColor: '#e91e63'}}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label className="form-label fw-bold mb-0">Promotion Image</label>
                                    <small className="text-muted">*Recommended: 1200x800px</small>
                                </div>
                                
                                <div className="text-center mb-3">
                                    {offerImageUploading['promotionImage'] ? (
                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                            <div>
                                                <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                <small>Uploading...</small>
                                            </div>
                                        </div>
                                    ) : offerImgData?.['promotionImage'] ? (
                                        <div className="position-relative">
                                            <img 
                                                src={`${API_BASE_URL}/${offerImgData['promotionImage']}`} 
                                                alt="Promotion Image" 
                                                className="img-fluid rounded-0" 
                                                style={{ maxHeight: '200px', width: 'auto', objectFit: 'cover' }}
                                            />
                                            <small className="text-success d-block mt-2">✓ Image uploaded</small>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center text-muted rounded-0" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                            <div>
                                                <FaImage className="fs-1 mb-2" style={{color: '#ccc'}} />
                                                <p className="mb-0" style={{fontSize: '12px'}}>No image uploaded</p>
                                                <p className="mb-0" style={{fontSize: '11px', color: '#999'}}>Click below to upload</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    name="promotionImage" 
                                    onChange={handleOfferImageUpload}
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                    disabled={offerImageUploading['promotionImage']}
                                />
                                <small className="text-muted d-block mt-2">JPG, PNG, or WebP • Max 5MB</small>
                            </div>
                        </div>

                        {/* Promotion Video */}
                        <div className="col-lg-6 col-md-6 col-sm-12 mb-4">
                            <div className="border rounded-0 p-3" style={{borderColor: '#e91e63'}}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label className="form-label fw-bold mb-0">Promotion Video</label>
                                    <small className="text-muted">*Recommended: 1920x1080px</small>
                                </div>
                                
                                <div className="text-center mb-3">
                                    {offerImageUploading['promotionVideo'] ? (
                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                            <div>
                                                <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                <small>Uploading...</small>
                                            </div>
                                        </div>
                                    ) : offerImgData?.['promotionVideo'] ? (
                                        <div className="position-relative">
                                            <video 
                                                src={`${API_BASE_URL}/${offerImgData['promotionVideo']}`} 
                                                className="img-fluid rounded-0" 
                                                style={{ maxHeight: '200px', width: 'auto', objectFit: 'cover' }}
                                                controls
                                            />
                                            <small className="text-success d-block mt-2">✓ Video uploaded</small>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center text-muted rounded-0" style={{ height: '200px', backgroundColor: '#f5f5f5' }}>
                                            <div>
                                                <FaUpload className="fs-1 mb-2" style={{color: '#ccc'}} />
                                                <p className="mb-0" style={{fontSize: '12px'}}>No video uploaded</p>
                                                <p className="mb-0" style={{fontSize: '11px', color: '#999'}}>Click below to upload</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <input 
                                    type="file" 
                                    accept="video/*" 
                                    name="promotionVideo" 
                                    onChange={handleOfferImageUpload}
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', padding: '7px 12px' }}
                                    disabled={offerImageUploading['promotionVideo']}
                                />
                                <small className="text-muted d-block mt-2">MP4, WebM, or OGG • Max 50MB</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderNotificationSettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <div className="row">
                    <div className="col-md-6">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '13px', fontWeight: '600', color: '#1a237e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Notifications</h6>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="newOrder" defaultChecked />
                            <label className="form-check-label" htmlFor="newOrder" style={{ fontSize: '13px', color: '#334155' }}>
                                New order notifications
                            </label>
                        </div>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="orderStatus" defaultChecked />
                            <label className="form-check-label" htmlFor="orderStatus" style={{ fontSize: '13px', color: '#334155' }}>
                                Order status updates
                            </label>
                        </div>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="lowStock" />
                            <label className="form-check-label" htmlFor="lowStock" style={{ fontSize: '13px', color: '#334155' }}>
                                Low stock alerts
                            </label>
                        </div>
                    </div>
                    
                    <div className="col-md-6">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '13px', fontWeight: '600', color: '#1a237e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>System Notifications</h6>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="loginAlerts" defaultChecked />
                            <label className="form-check-label" htmlFor="loginAlerts" style={{ fontSize: '13px', color: '#334155' }}>
                                Login alerts
                            </label>
                        </div>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="systemUpdates" />
                            <label className="form-check-label" htmlFor="systemUpdates" style={{ fontSize: '13px', color: '#334155' }}>
                                System updates
                            </label>
                        </div>
                        
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="maintenance" />
                            <label className="form-check-label" htmlFor="maintenance" style={{ fontSize: '13px', color: '#334155' }}>
                                Maintenance notifications
                            </label>
                        </div>
                    </div>
                </div>
                
                <div className="text-end">
                    <button
                        type="button"
                        className="btn theme-btn-blue-flat btn-sm rounded-0"
                        style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                        <FaSave size={13} /> Save Notifications
                    </button>
                </div>
            </div>
        </div>
    );

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwError('');
        setPwSuccess(false);

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPwError('All fields are required');
            return;
        }
        if (newPassword.length < 6) {
            setPwError('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwError('New password and confirm password do not match');
            return;
        }

        try {
            setPwLoading(true);
            await axios.post(`${API_BASE_URL}/admin/change-password`, {
                currentPassword,
                newPassword
            });
            setPwSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPwSuccess(false), 2500);
        } catch (err) {
            console.error('Change password error:', err);
            setPwError(err.response?.data?.message || 'Failed to change password');
        } finally {
            setPwLoading(false);
        }
    };

    const renderSecuritySettings = () => (
        <div className="card border-0 shadow-none rounded-0">
            <div className="card-body" style={{ padding: '20px' }}>
                <form onSubmit={handleChangePassword} autoComplete="off">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Current Password</label>
                                <input
                                    type="password"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    disabled={pwLoading}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>New Password</label>
                                <input
                                    type="password"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    disabled={pwLoading}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label fw-bold" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Confirm New Password</label>
                                <input
                                    type="password"
                                    className="form-control rounded-0"
                                    style={{ border: '1px solid #d1d5db', height: '38px', padding: '8px 12px', fontSize: '13px' }}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    disabled={pwLoading}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                    {pwError && <div className="alert alert-danger py-2 rounded-0 mb-3" style={{ fontSize: '13px' }}>{pwError}</div>}
                    <div className="text-end d-flex align-items-center justify-content-end gap-3">
                        {pwSuccess && (
                            <span className="text-success fw-bold" style={{ fontSize: '13px' }}>Password updated successfully!</span>
                        )}
                        <button
                            type="submit"
                            className="btn theme-btn-blue-flat btn-sm rounded-0"
                            style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                            disabled={pwLoading}
                        >
                            {pwLoading ? (
                                <>
                                    <FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Updating...
                                </>
                            ) : (
                                <>
                                    <FaSave size={13} /> Update Password
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">ADMIN SETTINGS</h4>
                            <p className="text-muted p-0 m-0">Manage application settings and configurations</p>
                        </div>
                    </div>
                </div>

                {/* Settings Tabs */}
                <div className="d-flex align-items-center gap-1 mt-0 mb-0" style={{ borderBottom: '1px solid #d1d5db', paddingLeft: '12px', backgroundColor: '#ffffff' }}>
                    {[
                        { key: 'banner', label: 'Banner Management', icon: <FaImage className="me-2" style={{ color: activeTab === 'banner' ? '#1a237e' : '#ffd600' }} /> },
                        { key: 'offers', label: 'Offer Images', icon: <FaImage className="me-2" style={{ color: activeTab === 'offers' ? '#1a237e' : '#ff6f00' }} /> },
                        { key: 'onboarding', label: 'Onboarding Screens', icon: <FaMobileAlt className="me-2" style={{ color: activeTab === 'onboarding' ? '#1a237e' : '#ffd600' }} /> },
                        { key: 'general', label: 'General Settings', icon: <FaCog className="me-2" style={{ color: activeTab === 'general' ? '#1a237e' : '#1a237e' }} /> },
                        { key: 'delivery', label: 'Delivery Rules', icon: <FaMapMarkerAlt className="me-2" style={{ color: activeTab === 'delivery' ? '#1a237e' : '#ffd600' }} /> },
                        { key: 'security', label: 'Security', icon: <FaLock className="me-2" style={{ color: activeTab === 'security' ? '#1a237e' : '#1a237e' }} /> }
                    ].map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    border: 'none',
                                    borderBottom: isActive ? '3px solid #1a237e' : '3px solid transparent',
                                    background: isActive ? '#e8eaf6' : 'transparent',
                                    color: isActive ? '#1a237e' : '#475569',
                                    fontWeight: isActive ? '700' : '600',
                                    fontSize: '13px',
                                    padding: '10px 16px',
                                    borderRadius: '0px',
                                    transition: 'all 0.2s',
                                    textTransform: 'uppercase',
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '-1px'
                                }}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content Panels */}
                <div className="row mt-0 settings-content-row">
                    <div className="col-12 p-0">
                        {activeTab === 'banner' && renderBannerSettings()}
                        {activeTab === 'offers' && renderOfferImagesSettings()}
                        {activeTab === 'onboarding' && renderOnboardingSettings()}
                        {activeTab === 'general' && renderGeneralSettings()}
                        {activeTab === 'delivery' && renderDeliverySettings()}
                        {activeTab === 'security' && renderSecuritySettings()}
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default SettingsPage;
